import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { combineLatest, firstValueFrom, Observable, Subscription } from 'rxjs';
import { UserService } from '../../../user/services';
import { QuantityUpdate } from 'src/app/shared/models/quantity-update';
import { ProductDetailTagsService } from '../../services/product-detail-tags.service';
import { TagProduct } from '../../../shared-product/components/usf-product-tag-list/models/tag-product.model';
import { getJITConfig } from '../../utils/product-summary.utils';
import { buildAlertMessage } from '../../utils/product-alert-message.utils';
import { ProductStatusReadOut } from '../../models/product-status-read-out';
import { buildProductStatusReadOut } from '../../utils/product-status-read-out.utils';
import { ProductAlertMessage } from '../../models/product-alert-message';
import { JitConfigMessage } from '../../models/jit-config-message.model';
import {
  ProductPropertiesEnum,
  ProductSummary,
} from '@usf/product-types/Product-Summary';
import { ProductDetailViewModel } from '../../models/product-detail-view-model';
import { OrderItem } from '@usf/ngrx-order';
import { ProductAlternativeTypeEnum } from '../../models/product-alternative';
import { ProductAlternativeSearchService } from '../../services/product-alternative-search.service';
import {
  PlatformService,
  PanAppState,
  jwtDecode,
  AuthConnectService,
} from '@panamax/app-state';
import { ListsPageData } from '../../../lists/model/lists-page.model';
import { ListsService } from '../../../lists/services/lists.service';
import { TranslateService } from '@ngx-translate/core';
import { UsfProductCardModeEnum } from 'src/app/shared/constants/usf-product-card-mode.enum';
import { FEATURES } from '../../../shared/constants/splitio-features';
import { SellerCreateOpportunityService } from '../../services/seller-create-opportunity.service';
import { defaultJITConfig } from 'src/app/shared/constants/jit-config';
import { filter, map, take } from 'rxjs/operators';
import { EllipsisClickData } from '../../../shared/models/usf-product-card.model';
import { DealsProductCardService } from '../../../shared/services/product-card/deals-product-card.service';
import { ItemTypes } from '../../../lists/shared/list-detail-management/model/list-detail-management-view.model';
import { ProductDetailService } from '@product-detail/services/product-detail.service';
import { customerIsGLCustomer } from '../../../shared/helpers/customer.helpers';
import { CustomPricingRationaleService } from '@product-detail/services/custom-pricing-rationale.service';
import { SellerPriceDetails } from '@usf/price-types';
import { ProductAnalyticsService } from '@usf/ngrx-product';
import { ActionSheetController } from '@ionic/angular';
import { PRODUCT_DETAIL } from '@product-detail/constants/constants';
import { ToggleNewSearchService } from '@shared/services/toggle-new-search/toggle-new-search.service';
import { ProductShippingPriceService } from '@shared/services/product-shipping-price/product-shipping-price.service';
import { SEARCH_PAGES_ENUM } from '@shared/constants/search-pages.enum';
import { AddToListModalService } from '@shared/services/dual-pane/add-to-list-modal.service';
import { NavigationHelperService } from '@shared/helpers/navigation.helpers.service';
import { BRAND_FACET } from '@app/new-search/constants/new-search-constants';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-product-detail-summary',
  templateUrl: './product-detail-summary.component.html',
  styleUrls: ['./product-detail-summary.component.scss'],
})
export class ProductDetailSummaryComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() product: ProductDetailViewModel;
  @Input() sellerPriceDetail: SellerPriceDetails;
  @Input() divisionalPromotions: string[] = [];
  @Input() orderItem: OrderItem;
  @Input() pricingDisabled: boolean;
  @Input() isInventory: boolean;
  @Input() isGuestUser: boolean;
  @Input() newSearchEnabled = false;
  @Input() isFavorite = false;
  @Input() isFavoriteButtonEnabled = true;
  @Output() tabSelectionEmitter = new EventEmitter<number>();
  @Output() quantityUpdateEmitter = new EventEmitter<QuantityUpdate>();
  @Output() addProductsToListEmitter = new EventEmitter<any>();
  @Output() shareButtonPressEmitter = new EventEmitter<any>();
  @Output() printButtonPressEmitter = new EventEmitter();
  @Output() favoriteButtonPressEmitter = new EventEmitter<boolean>();

  // Constants
  readonly propertiesEnum = ProductPropertiesEnum;
  readonly productAlternativeTypes = ProductAlternativeTypeEnum;

  // Feature Flags
  createOpportunityFeatureFlag = FEATURES.split_global_create_opportunity.name;
  productReviewsFeatureFlag = FEATURES.split_global_product_reviews.name;
  shareFeatureFlag = FEATURES.split_global_share_pdp.name;
  favoritesFeatureFlag = FEATURES.split_dynamic_add_to_favorites.name;
  shareDesktopFeatureFlag = FEATURES.split_global_share_pdp_desktop.name;
  printFeatureFlag = FEATURES.split_global_print_pdp.name;
  moreFromThisBrandFlag = FEATURES.split_global_more_from_this_brand.name;

  // Component Variables
  alertMessage: ProductAlertMessage;
  currentTab = 1;
  customerProductNumber: string;
  displayInventory = true;
  displayTags: TagProduct[] = [];
  isMslRestricted = false;
  isOGRestricted = false;
  isRestock = true;
  jitVendorMessage: JitConfigMessage = defaultJITConfig;
  listPageData: ListsPageData;
  productDescriptionNotAvailableMessage: string;
  quantityBoxesDisabledStatus: boolean;
  readOut: ProductStatusReadOut;
  userType$: Observable<any>;
  isPrintButtonEnabled = false;
  isCommonCprModalEnabled = false;
  isDirectAllowReturnsEnabled;
  isProductShippingPriceEnabled$: Observable<boolean>;
  isNewSearchEnabled = false;
  showBackButton = false;

  // Subscriptions
  listPageDataSub$: Subscription = new Subscription();
  selectedCustomerSub$: Subscription = new Subscription();
  customerNumberSub$: Subscription = new Subscription();
  appStateForProduct$: Subscription = new Subscription();
  featureFlagSub$: Subscription = new Subscription();

  constructor(
    readonly userService: UserService,
    readonly platformService: PlatformService,
    private productDetailTagsService: ProductDetailTagsService,
    private productAlternativeSearchService: ProductAlternativeSearchService,
    private listsService: ListsService,
    private translateService: TranslateService,
    private sellerCreateOpportunityService: SellerCreateOpportunityService,
    private customPricingRationale: CustomPricingRationaleService,
    private panAppState: PanAppState,
    private dealsProductCardService: DealsProductCardService,
    private productDetailService: ProductDetailService,
    private productAnalyticsService: ProductAnalyticsService,
    private actionSheetCtrl: ActionSheetController,
    private authConnectService: AuthConnectService,
    private navigationHelperService: NavigationHelperService,
    readonly addToListModalService: AddToListModalService,
    private readonly toggleNewSearchService: ToggleNewSearchService,
    private readonly productShippingPriceService: ProductShippingPriceService,
  ) {
    this.translate();
  }

  ngOnInit() {
    this.getUserType();
    this.selectedCustomerSub$ = this.panAppState.customer$.subscribe(
      customer => {
        this.isMslRestricted = customer.restrictToOG === 'M';
        this.isOGRestricted = customer.restrictToOG === 'Y';
      },
    );
    this.updateTags();
    this.checkForUpdates();

    this.listPageDataSub$ = this.listsService.listsView$().subscribe(data => {
      this.listPageData = data;
    });

    this.populateCustomerProductNumber();

    this.featureFlagSub$ = combineLatest([
      this.panAppState.feature$([this.printFeatureFlag]),
      this.panAppState.feature$([
        FEATURES.split_global_direct_allow_returns.name,
      ]),
      this.panAppState.feature$([
        FEATURES.split_global_cpr_override_common_component.name,
      ]),
    ]).subscribe(
      ([
        isPrintButtonEnabled,
        directAllowReturnsFeatureFlagEnabled,
        isCommonCprModalEnabled,
      ]) => {
        this.isPrintButtonEnabled = isPrintButtonEnabled;
        this.isDirectAllowReturnsEnabled = directAllowReturnsFeatureFlagEnabled;
        this.isCommonCprModalEnabled = isCommonCprModalEnabled;
      },
    );

    this.isProductShippingPriceEnabled$ =
      this.productShippingPriceService.isProductShippingPriceEnabled$();
    this.setShowBackButton();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.product?.currentValue) {
      this.checkForUpdates();
    }
    if (changes.productSubstitute?.currentValue) {
      this.checkForUpdates();
    }
  }

  translate(): void {
    this.productDescriptionNotAvailableMessage = this.translateService.instant(
      'i18n.productDetail.productDescriptionNotAvailableMessage',
    );
  }

  updateJitVendorMessage(): void {
    if (this.product?.summary) {
      this.jitVendorMessage = getJITConfig(this.product);
    }
  }

  toggleTab(tab: number) {
    if (this.currentTab !== tab) {
      this.currentTab = this.currentTab === 1 ? 2 : 1;
    }
    this.tabSelectionEmitter.emit(this.currentTab);
  }

  changeQuantityHandler(quantityUpdate: QuantityUpdate) {
    this.quantityUpdateEmitter.emit(quantityUpdate);
  }

  async showAllAvailableStatuses() {
    await this.productDetailTagsService.showAllAvailableStatuses(this.product);
  }

  searchReplacement(productSummary: ProductSummary, isSubstitute: boolean) {
    this.toggleNewSearchService
      .isNewSearchEnabled()
      .pipe(take(1))
      .subscribe(newSearchEnabled => {
        if (newSearchEnabled) {
          this.productAlternativeSearchService.newSearchReplacement(
            productSummary,
            isSubstitute,
          );
        } else {
          this.productAlternativeSearchService.searchReplacement(
            productSummary,
            UsfProductCardModeEnum.replacementSearch,
            false,
            null,
            SEARCH_PAGES_ENUM.pdp,
          );
        }
      });
  }

  async openAddToListModal() {
    this.addToListModalService.openModalViaController(
      [this.product],
      false,
      false,
      PRODUCT_DETAIL,
    );
  }

  async openSellerCreateAnOpportunityModal() {
    await this.sellerCreateOpportunityService.showCreateOpportunityModal(
      this.product,
    );
  }

  async openCustomPricingRationaleModal() {
    const { divisionNumber, productNumber } = this.product?.summary;
    const { trackingAttributes } = this.product;
    this.productAnalyticsService.trackUserOpenCPRModel(
      divisionNumber,
      productNumber,
      trackingAttributes,
    );
    await this.customPricingRationale.showCustomPricingRationaleModal(
      this.product,
      this.sellerPriceDetail,
      this.isCommonCprModalEnabled,
    );
  }

  async openCarouselImagesModal(activeSlide: number) {
    await this.productDetailService.showCarouselImagesModal(
      this.product,
      activeSlide,
    );
  }

  private checkForUpdates(): void {
    this.updateReadOut();
    this.updateQuantityBoxesDisabledStatus();
    this.updateAlerts();
    this.updateJitVendorMessage();
    this.updateInventoryDisplayableStatus();
    this.updateTags();
  }

  private updateTags(): void {
    const CLICKABLE = true;
    this.displayTags = this.productDetailTagsService.getDisplayTags(
      this.product?.summary,
      this.product?.inventory,
      this.product?.contract,
      this.product?.customerPill,
      this.product?.recentPurchase,
      this.product?.trackingAttributes,
      this.product?.resultRank,
      CLICKABLE,
      this.isGuestUser,
    );
  }

  private getUserType(): void {
    this.userType$ = this.panAppState.accessToken$.pipe(
      filter(token => !!token),
      take(1),
      map(token => {
        const decoded = jwtDecode(token);
        if (decoded) {
          return decoded['usf-claims'].userType;
        }
        return '';
      }),
    );
  }

  private updateReadOut(): void {
    this.readOut = buildProductStatusReadOut(this.product);
  }

  private updateAlerts(): void {
    this.appStateForProduct$ = this.productDetailService
      .getAppStateForProduct()
      .subscribe(appStateForProduct => {
        this.alertMessage = buildAlertMessage(
          this.product,
          false,
          this.isMslRestricted,
          this.isOGRestricted,
          false,
          appStateForProduct.isForceRegularOrderProduct,
          appStateForProduct.isProprietaryUIEnabled,
          this.platformService.platformType ===
            this.platformService.platformEnum.desktop,
        );
      });
  }

  private updateQuantityBoxesDisabledStatus(): void {
    this.quantityBoxesDisabledStatus = !this.product?.orderable;
  }

  private updateInventoryDisplayableStatus(): void {
    // When base product is status 2 and replacement is status 3
    if (
      this.product?.alternative?.required &&
      this.product?.alternative?.available &&
      this.product?.alternative?.prioritizeAlternative
    ) {
      this.displayInventory = false;
    }
  }

  shouldDisplayOverview(): boolean {
    return (
      this.jitVendorMessage?.showMessage ||
      this.displayTags?.length > 0 ||
      this.readOut?.display ||
      this.product?.summary?.poDates?.length > 0
    );
  }

  populateCustomerProductNumber(): void {
    let isGLCustomer = customerIsGLCustomer(this.product?.selectedCustomer);

    this.customerNumberSub$ = this.productDetailService
      .getProductDetailViewModel(this.product?.productNumber, false)
      .subscribe(data => {
        if (
          data.hasOwnProperty('customerProductNumber') &&
          data?.customerProductNumber
        ) {
          this.customerProductNumber = isGLCustomer
            ? 'GL: #' + data.customerProductNumber
            : 'Cust. Product: #' + data.customerProductNumber;
        }
      });
  }

  clickPromotions(event: Event) {
    const ellipsisClickData = {
      event,
      product: this.product,
      mode: UsfProductCardModeEnum.defaultSearch,
      pricingDisabled: false,
      isSelected: false,
      itemType: ItemTypes.product,
      list: null,
      isTouch: this.platformService.isTouch.value,
    } as EllipsisClickData;
    this.dealsProductCardService.openQuickViewModal(
      ellipsisClickData,
      'productStatus',
    );
  }

  async loginGuest(): Promise<void> {
    await this.authConnectService.logout();
  }

  openLearnMore() {
    this.productAnalyticsService.trackLearnMoreButtonClick();
    window.open(environment.learnMoreUrl, '_blank');
  }

  ngOnDestroy(): void {
    if (this.listPageDataSub$) {
      this.listPageDataSub$.unsubscribe();
    }
    if (this.selectedCustomerSub$) {
      this.selectedCustomerSub$.unsubscribe();
    }
    if (this.customerNumberSub$) {
      this.customerNumberSub$.unsubscribe();
    }
    if (this.featureFlagSub$) {
      this.featureFlagSub$.unsubscribe();
    }
  }

  async onShareButtonPress(event: Event, isEllipsisClick: boolean = true) {
    if (
      this.platformService.platformType ===
        this.platformService.platformEnum.mobile &&
      isEllipsisClick
    ) {
      const actionSheet = await this.actionSheetCtrl.create({
        cssClass: 'action-sheet-touch',
        mode: 'ios',
        buttons: [
          {
            text: this.translateService.instant('i18n.common.share'),
            handler: () => {
              this.shareButtonPressEmitter.emit();
            },
          },
          ...(this.isPrintButtonEnabled
            ? [
                {
                  text: this.translateService.instant('i18n.common.print'),
                  handler: () => {
                    this.printButtonPressEmitter.emit();
                  },
                },
              ]
            : []),
          {
            text: this.translateService.instant('i18n.common.cancel'),
            role: 'cancel',
            cssClass: 'cancel-button',
          },
        ],
      });
      await actionSheet.present();
    } else if (
      this.platformService.platformType ===
      this.platformService.platformEnum.tablet
    ) {
      this.shareButtonPressEmitter.emit();
    } else {
      //DESKTOP OR MOBILE WITHOUT ELLIPSIS CLICK
      this.shareButtonPressEmitter.emit();
    }
  }

  onPrintButtonPress() {
    this.printButtonPressEmitter.emit();
  }

  trackRatingandReview(): void {
    this.productAnalyticsService.trackRatingsAndReviewsUserEngagement(
      this.product?.summary?.divisionNumber,
      this.product?.productNumber,
      this.product?.trackingAttributes,
    );
  }

  searchBrand(brand: string) {
    this.productAnalyticsService.trackLinkClick(brand);
    if (this.newSearchEnabled) {
      this.navigationHelperService.routeToNewSearch({
        facetFilters: `${BRAND_FACET}: ${brand}`,
      });
    } else {
      this.navigationHelperService.routeToSearch({
        dimensionId: 0,
        pageType: 'all',
        brandName: encodeURIComponent(brand),
      });
    }
  }

  setShowBackButton() {
    firstValueFrom(this.toggleNewSearchService.isNewSearchEnabled()).then(
      newSearchEnabled => {
        this.showBackButton =
          this.platformService?.platformType ===
            this.platformService?.platformEnum?.desktop &&
          newSearchEnabled &&
          'true' ===
            this.navigationHelperService?.getQueryParams()?.get('isSearch');
      },
    );
  }

  onAddToFavoritesButtonPress() {
    if (!this.isFavoriteButtonEnabled) {
      return;
    }
    this.favoriteButtonPressEmitter.emit(!this.isFavorite);
  }
}
